import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Container, LinearProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { localGet } from '../../../utils/localStorageUtils';
import {
  ROWS_PER_REQUEST, TEMPLATE_CSV, csvToRows, demographicsSummary, uploadDemographics,
} from '../../../utils/demographicsService';

/**
 * Student demographics (phase 4, Oct 2026). DiscloSure admins upload the district's spreadsheet
 * (grade, gender, race, ethnicity) at onboarding. Values are stored on the server only and never
 * shown here — just how many students have data. Gender appears to responders on emergency
 * reports only. The Clever SIS sync fills the same store automatically once connected.
 */
const DemographicsPanel = ({ data }) => {
  const theme = useTheme();
  const fileRef = useRef(null);
  const [user, setUser] = useState(null);
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState(null); // { done, total }
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const district = data?.id;
  useEffect(() => { (async () => setUser(await localGet('user', true)))(); }, []);
  const isAdmin = user?.permission === 'disclosureAdmin';

  const refresh = async () => {
    if (!district) return;
    const s = await demographicsSummary(district);
    if (s.ok) setSummary(s);
  };
  useEffect(() => { if (isAdmin) refresh(); /* eslint-disable-next-line */ }, [isAdmin, district]);

  if (!isAdmin) return null;

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'disclosure-student-demographics-template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError(''); setResult(null);
    const { rows, missing } = csvToRows(await file.text());
    if (missing.length) { setError(`The spreadsheet needs ${missing.join(' and ')}.`); return; }
    if (!rows.length) { setError('The spreadsheet has no student rows.'); return; }
    const totals = { received: 0, matched: 0, unmatched: [], empty: [] };
    setProgress({ done: 0, total: rows.length });
    for (let start = 0; start < rows.length; start += ROWS_PER_REQUEST) {
      const chunk = rows.slice(start, start + ROWS_PER_REQUEST);
      const res = await uploadDemographics(district, chunk);
      if (!res.ok) {
        setError(`${res.message} (stopped at row ${start + 1}; rows before that were saved).`);
        break;
      }
      totals.received += res.received;
      totals.matched += res.matched;
      // Server row numbers are per chunk; +1 more for the header row so they match the sheet.
      totals.unmatched.push(...res.unmatched.map((n) => n + start + 1));
      totals.empty.push(...res.empty.map((n) => n + start + 1));
      setProgress({ done: Math.min(start + chunk.length, rows.length), total: rows.length });
    }
    setProgress(null);
    setResult(totals);
    refresh();
  };

  const border = `1px solid ${theme.palette.custom?.gray || '#ccc'}`;
  const muted = { fontSize: '14px', color: theme.palette.text?.secondary || '#666', lineHeight: 1.5 };
  const listRows = (nums) => (nums.length > 20 ? `${nums.slice(0, 20).join(', ')} and ${nums.length - 20} more` : nums.join(', '));

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', marginLeft: '60px', marginRight: '60px', marginTop: '40px',
                     width: 'auto', paddingLeft: '0px !important', border, borderRadius: '4px' }}>
      <Box sx={{ borderBottom: border }}>
        <Typography sx={{ fontSize: '20px', fontWeight: 700, margin: '20px' }}>Student demographics</Typography>
      </Box>
      <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Typography sx={muted}>
          Upload the district's spreadsheet of grade, gender, race and ethnicity. Students are matched by email
          (or student number). These details are stored on the server only: they never appear in the app or
          portal, except gender, which responders see on emergency reports. Trends use them only with small
          groups hidden.
        </Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          {summary ? `${summary.studentsWithData} students have demographics on file`
                     + (summary.lastUpdated ? ` (last updated ${new Date(summary.lastUpdated).toLocaleDateString()})` : '')
                   : 'Loading…'}
        </Typography>
        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Button variant="contained" disabled={!!progress} onClick={() => fileRef.current && fileRef.current.click()}
                  sx={{ textTransform: 'none', boxShadow: 'none' }}>
            Upload spreadsheet (.csv)
          </Button>
          <Button variant="outlined" onClick={downloadTemplate} sx={{ textTransform: 'none' }}>
            Download template
          </Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onFile} />
        </Box>
        {progress && (
          <Box>
            <LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} />
            <Typography sx={muted}>Uploading {progress.done} of {progress.total} rows…</Typography>
          </Box>
        )}
        {result && (
          <Box sx={{ fontSize: '14px' }}>
            <Typography sx={{ fontSize: '14px' }}>
              Saved demographics for <strong>{result.matched}</strong> of {result.received} rows.
            </Typography>
            {result.unmatched.length > 0 && (
              <Typography sx={muted}>Not matched to a student in this district (sheet rows): {listRows(result.unmatched)}</Typography>
            )}
            {result.empty.length > 0 && (
              <Typography sx={muted}>Matched but had no values (sheet rows): {listRows(result.empty)}</Typography>
            )}
          </Box>
        )}
        {error && <Typography sx={{ fontSize: '13px', color: theme.palette.error?.main || '#d32f2f' }}>{error}</Typography>}
      </Box>
    </Container>
  );
};

export default DemographicsPanel;
